import React from 'react';
import { Container, Typography, Box, Paper, Divider, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const CookiePolicy = () => {
  const navigate = useNavigate();
  const { consent, acceptCookies, declineCookies } = useCookieConsent();

  return (
    <>
      <Helmet>
        <title>Süti (cookie) szabályzat – Wedigo</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Süti (cookie) szabályzat
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Utolsó frissítés: 2026. november
        </Typography>

        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Mi az a süti?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            A sütik (angolul cookie) kis szövegfájlok, amelyeket a weboldal a böngészőjén keresztül tárol
            az eszközén. Segítségükkel az oldal emlékezik bizonyos információkra – például hogy be van
            jelentkezve, vagy hogy korábban már járt az oldalon.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Milyen sütiket használunk?
          </Typography>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            1. Szükséges sütik (hozzájárulás nélkül)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Ezek a sütik az oldal alapvető működéséhez nélkülözhetetlenek. Nem gyűjtenek személyes
            azonosításra alkalmas adatokat, és nem kapcsolhatók ki.
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Munkamenet-sütik</strong> – bejelentkezési állapot megőrzése
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Barion Pixel (Base)</strong> – a Barion Payment Zrt. csalásmegelőzési kódja, amelyet
                a Barion online fizetés elfogadásához kötelező minden oldalon elhelyezni. A böngésző- és
                eszközadatokat közvetlenül a Barionnak küldi (jogalap: a Barion jogos érdeke). Marketing
                célú (Full) Barion Pixelt nem használunk. Részletek:{' '}
                <a href="https://www.barion.com/hu/adatvedelmi-tajekoztato/" target="_blank" rel="noopener noreferrer">Barion adatkezelési tájékoztató</a>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>wedigo_cookie_consent</strong> – az Ön sütibeleegyezési döntésének tárolása (localStorage)
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            2. Analitikai sütik (hozzájárulással)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Ezeket a sütiket csak az Ön kifejezett hozzájárulásával aktiváljuk. Arra szolgálnak, hogy
            megértsük hogyan használják látogatóink az oldalt, és ennek alapján fejleszthessük a
            felhasználói élményt.
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <Box component="thead">
                <Box component="tr" sx={{ backgroundColor: 'grey.100' }}>
                  {['Süti neve', 'Szolgáltató', 'Cél', 'Lejárat'].map((h) => (
                    <Box key={h} component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, border: '1px solid', borderColor: 'divider' }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {[
                  ['_ga', 'Google Analytics', 'Egyedi látogatók megkülönböztetése', '2 év'],
                  ['_ga_XXXXXXX', 'Google Analytics', 'Munkamenet-azonosítás', '2 év'],
                  ['_clck', 'Microsoft Clarity', 'Felhasználói munkamenet azonosítása', '1 év'],
                  ['_clsk', 'Microsoft Clarity', 'Munkamenet összevonása', '1 nap'],
                ].map(([name, provider, purpose, expires]) => (
                  <Box component="tr" key={name}>
                    <Box component="td" sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', fontFamily: 'monospace' }}>{name}</Box>
                    <Box component="td" sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>{provider}</Box>
                    <Box component="td" sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>{purpose}</Box>
                    <Box component="td" sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>{expires}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Hogyan kezelheti a sütiket?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Hozzájárulását bármikor visszavonhatja az alábbi gombokkal. A módosítás az oldal következő
            betöltésekor lép életbe. A böngészőjében is beállíthatja a sütik blokkolását, azonban ez
            egyes funkciók működését befolyásolhatja.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button
              variant="contained"
              onClick={acceptCookies}
              disabled={consent === true}
              sx={{ borderRadius: 2 }}
            >
              {consent === true ? 'Jelenleg: Elfogadva' : 'Elfogadom az analitikát'}
            </Button>
            <Button
              variant="outlined"
              onClick={declineCookies}
              disabled={consent === false}
              sx={{ borderRadius: 2 }}
            >
              {consent === false ? 'Jelenleg: Elutasítva' : 'Elutasítom'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" fontWeight={600} gutterBottom>
            Adatkezelő
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Wedigo – esküvői szolgáltatói platform
            <br />
            Kapcsolat: <strong>wedigo@wedigo.hu</strong>
          </Typography>

        </Paper>

        <Button sx={{ mt: 3 }} onClick={() => navigate(-1)} color="primary">
          ← Vissza
        </Button>
      </Container>
    </>
  );
};

export default CookiePolicy;
